import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface FeeAssignment {
  id: string;
  studentId: string;
  studentName: string;
  studentClass: string;
  rollNumber: string;
  totalAmount: number;
  totalPaid: number;
  totalPending: number;
  status: string;
  accountDisabled?: boolean;
  disabledAt?: string;
  disabledReason?: string;
  installments: Array<{
    id: string;
    name: string;
    amount: number;
    dueDate: string;
    status: string;
    paidAmount: number;
  }>;
}

interface FeeCollectionProps {
  domain: string;
}

const FeeCollection: React.FC<FeeCollectionProps> = ({ domain }) => {
  const [feeAssignments, setFeeAssignments] = useState<FeeAssignment[]>([]);
  const [filteredAssignments, setFilteredAssignments] = useState<FeeAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [classes, setClasses] = useState<string[]>([]);
  const [selectedAssignment, setSelectedAssignment] = useState<FeeAssignment | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [transactionId, setTransactionId] = useState('');

  // Disable account state
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disableReason, setDisableReason] = useState('');
  const [filterAccountStatus, setFilterAccountStatus] = useState('');
  const [filterOverdue, setFilterOverdue] = useState(false);

  // Calculate overdue amount for an assignment
  const calculateOverdueAmount = (assignment: FeeAssignment): number => {
    if (!assignment.installments || assignment.installments.length === 0) {
      // If no installments, check if total is overdue (use a default due date logic)
      return 0;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let overdueAmount = 0;
    assignment.installments.forEach((installment) => {
      if (installment.status !== 'paid') {
        const dueDate = new Date(installment.dueDate);
        dueDate.setHours(0, 0, 0, 0);
        if (dueDate < today) {
          // This installment is overdue
          const unpaidAmount = installment.amount - (installment.paidAmount || 0);
          if (unpaidAmount > 0) {
            overdueAmount += unpaidAmount;
          }
        }
      }
    });

    return overdueAmount;
  };

  // Check if assignment has overdue amount
  const hasOverdue = (assignment: FeeAssignment): boolean => {
    return calculateOverdueAmount(assignment) > 0;
  };

  useEffect(() => {
    if (domain) {
      loadFeeAssignments();
    }
  }, [domain]);

  useEffect(() => {
    filterAssignments();
  }, [feeAssignments, searchTerm, filterClass, filterStatus, filterAccountStatus, filterOverdue]);

  const loadFeeAssignments = async () => {
    setLoading(true);
    try {
      const feeAssignmentsRef = collection(db, `school-users/${domain}/fee-assignments`);
      const snapshot = await getDocs(feeAssignmentsRef);

      const assignments: FeeAssignment[] = [];
      const classSet = new Set<string>();

      snapshot.forEach((doc) => {
        const data = { id: doc.id, ...doc.data() } as FeeAssignment;
        assignments.push(data);
        classSet.add(data.studentClass);
      });

      setFeeAssignments(assignments);
      setClasses(Array.from(classSet).sort());
    } catch (error) {
      console.error('Error loading fee assignments:', error);
      alert('Failed to load fee assignments');
    } finally {
      setLoading(false);
    }
  };

  const filterAssignments = () => {
    let filtered = [...feeAssignments];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (a) =>
          a.studentName.toLowerCase().includes(searchLower) ||
          a.rollNumber.toLowerCase().includes(searchLower)
      );
    }

    if (filterClass) {
      filtered = filtered.filter((a) => a.studentClass === filterClass);
    }

    if (filterStatus) {
      filtered = filtered.filter((a) => a.status === filterStatus);
    }

    if (filterAccountStatus) {
      if (filterAccountStatus === 'disabled') {
        filtered = filtered.filter((a) => a.accountDisabled === true);
      } else if (filterAccountStatus === 'active') {
        filtered = filtered.filter((a) => !a.accountDisabled);
      }
    }

    if (filterOverdue) {
      filtered = filtered.filter((a) => hasOverdue(a));
    }

    setFilteredAssignments(filtered);
  };

  // Calculate total overdue amount across all assignments
  const getTotalOverdue = (): number => {
    return feeAssignments.reduce((sum, a) => sum + calculateOverdueAmount(a), 0);
  };

  // Get count of students with overdue fees
  const getOverdueCount = (): number => {
    return feeAssignments.filter((a) => hasOverdue(a)).length;
  };

  const handlePayment = (assignment: FeeAssignment) => {
    setSelectedAssignment(assignment);
    setPaymentAmount(String(assignment.totalPending));
    setShowPaymentModal(true);
  };

  const processPayment = async () => {
    if (!selectedAssignment) return;

    const amount = Number(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }

    if (amount > selectedAssignment.totalPending) {
      alert('Payment amount cannot exceed pending amount');
      return;
    }

    setLoading(true);
    try {
      const newTotalPaid = selectedAssignment.totalPaid + amount;
      const newTotalPending = selectedAssignment.totalPending - amount;
      const newStatus = newTotalPending === 0 ? 'paid' : 'partial';

      // Update fee assignment
      const assignmentRef = doc(db, `school-users/${domain}/fee-assignments`, selectedAssignment.id);
      await updateDoc(assignmentRef, {
        totalPaid: newTotalPaid,
        totalPending: newTotalPending,
        status: newStatus,
        lastPaymentDate: new Date().toISOString(),
        lastPaymentAmount: amount,
        lastPaymentMode: paymentMode,
        lastTransactionId: transactionId || null,
        updatedAt: new Date().toISOString(),
      });

      alert('Payment recorded successfully!');
      setShowPaymentModal(false);
      setSelectedAssignment(null);
      setPaymentAmount('');
      setTransactionId('');
      loadFeeAssignments();
    } catch (error) {
      console.error('Error processing payment:', error);
      alert('Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  const handleDisableAccount = (assignment: FeeAssignment) => {
    setSelectedAssignment(assignment);
    setDisableReason('');
    setShowDisableModal(true);
  };

  const toggleAccountStatus = async (enable: boolean = false, assignmentToUpdate?: FeeAssignment) => {
    const assignment = assignmentToUpdate || selectedAssignment;
    if (!assignment) return;

    if (!enable && !disableReason.trim()) {
      alert('Please provide a reason for disabling the account');
      return;
    }

    setLoading(true);
    try {
      const assignmentRef = doc(db, `school-users/${domain}/fee-assignments`, assignment.id);

      if (enable) {
        // Enable the account
        await updateDoc(assignmentRef, {
          accountDisabled: false,
          disabledAt: null,
          disabledReason: null,
          enabledAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
        alert('Account enabled successfully!');
      } else {
        // Disable the account
        await updateDoc(assignmentRef, {
          accountDisabled: true,
          disabledAt: new Date().toISOString(),
          disabledReason: disableReason,
          updatedAt: new Date().toISOString(),
        });
        alert('Account disabled successfully!');
      }

      setShowDisableModal(false);
      setSelectedAssignment(null);
      setDisableReason('');
      loadFeeAssignments();
    } catch (error) {
      console.error('Error toggling account status:', error);
      alert('Failed to update account status');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-success';
      case 'partial':
        return 'bg-warning';
      case 'pending':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <h5 className="mb-0">
          <i className="bi bi-cash-coin me-2 text-primary-custom"></i>
          Fee Collection & Payment Tracking
        </h5>
        <small className="text-muted">Collect fees and track payment status</small>
      </div>

      <div className="card-body">
        {/* Filters */}
        <div className="row g-3 mb-4">
          <div className="col-md-3">
            <input
              type="text"
              className="form-control"
              placeholder="Search by name or roll no..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <select
              className="form-select"
              value={filterClass}
              onChange={(e) => setFilterClass(e.target.value)}
            >
              <option value="">All Classes</option>
              {classes.map((cls) => (
                <option key={cls} value={cls}>Class {cls}</option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <select
              className="form-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All Payment Status</option>
              <option value="pending">Pending</option>
              <option value="partial">Partial</option>
              <option value="paid">Paid</option>
            </select>
          </div>
          <div className="col-md-2">
            <select
              className="form-select"
              value={filterAccountStatus}
              onChange={(e) => setFilterAccountStatus(e.target.value)}
            >
              <option value="">All Accounts</option>
              <option value="active">Active Only</option>
              <option value="disabled">Disabled Only</option>
            </select>
          </div>
          <div className="col-md-3 d-flex gap-2">
            <button
              className="btn flex-grow-1"
              style={{
                backgroundColor: filterOverdue ? '#fd7e14' : 'transparent',
                borderColor: '#fd7e14',
                color: filterOverdue ? '#fff' : '#fd7e14'
              }}
              onClick={() => setFilterOverdue(!filterOverdue)}
              title="Show only overdue fees"
            >
              <i className="bi bi-exclamation-triangle me-1"></i>
              Overdue {getOverdueCount() > 0 && <span className="badge bg-danger">{getOverdueCount()}</span>}
            </button>
            <button
              className="btn btn-outline-primary"
              onClick={loadFeeAssignments}
              disabled={loading}
              title="Refresh"
            >
              <i className="bi bi-arrow-clockwise"></i>
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="row g-3 mb-4">
          <div className="col-md-3">
            <div className="card border-success">
              <div className="card-body">
                <h6 className="text-muted">Total Collected</h6>
                <h4 className="text-success mb-0">
                  {formatCurrency(feeAssignments.reduce((sum, a) => sum + a.totalPaid, 0))}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-danger">
              <div className="card-body">
                <h6 className="text-muted">Total Pending</h6>
                <h4 className="text-danger mb-0">
                  {formatCurrency(feeAssignments.reduce((sum, a) => sum + a.totalPending, 0))}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card" style={{ borderColor: '#fd7e14' }}>
              <div className="card-body">
                <h6 className="text-muted">
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  Total Overdue
                </h6>
                <h4 className="mb-0" style={{ color: '#fd7e14' }}>
                  {formatCurrency(getTotalOverdue())}
                </h4>
                <small className="text-muted">{getOverdueCount()} students</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-primary">
              <div className="card-body">
                <h6 className="text-muted">Total Students</h6>
                <h4 className="text-primary mb-0">{feeAssignments.length}</h4>
              </div>
            </div>
          </div>
        </div>

        {/* Fee Assignments Table */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : filteredAssignments.length === 0 ? (
          <div className="text-center text-muted py-5">
            <i className="bi bi-inbox fs-1 d-block mb-3"></i>
            <p>{feeAssignments.length === 0 ? 'No fee assignments found. Assign fees to students first.' : 'No results match your filters.'}</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th>Roll No</th>
                  <th>Student Name</th>
                  <th>Class</th>
                  <th>Total Amount</th>
                  <th>Paid</th>
                  <th>Pending</th>
                  <th>Overdue</th>
                  <th>Payment Status</th>
                  <th>Account</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssignments.map((assignment) => (
                  <tr
                    key={assignment.id}
                    className={assignment.accountDisabled ? 'table-secondary' : ''}
                  >
                    <td>{assignment.rollNumber}</td>
                    <td>
                      {assignment.studentName}
                      {assignment.accountDisabled && (
                        <i className="bi bi-slash-circle text-danger ms-2" title="Account Disabled"></i>
                      )}
                    </td>
                    <td>{assignment.studentClass}</td>
                    <td>{formatCurrency(assignment.totalAmount)}</td>
                    <td className="text-success">{formatCurrency(assignment.totalPaid)}</td>
                    <td className="text-danger">{formatCurrency(assignment.totalPending)}</td>
                    <td>
                      {calculateOverdueAmount(assignment) > 0 ? (
                        <span className="fw-bold" style={{ color: '#fd7e14' }}>
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          {formatCurrency(calculateOverdueAmount(assignment))}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadge(assignment.status)}`}>
                        {assignment.status.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      {assignment.accountDisabled ? (
                        <span className="badge bg-danger">
                          <i className="bi bi-x-circle me-1"></i>Disabled
                        </span>
                      ) : (
                        <span className="badge bg-success">
                          <i className="bi bi-check-circle me-1"></i>Active
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button
                          className="btn btn-primary"
                          onClick={() => handlePayment(assignment)}
                          disabled={assignment.status === 'paid' || assignment.accountDisabled}
                          title={assignment.accountDisabled ? 'Account is disabled' : 'Collect Payment'}
                        >
                          <i className="bi bi-cash"></i>
                        </button>
                        {assignment.accountDisabled ? (
                          <button
                            className="btn btn-success"
                            onClick={() => {
                              if (confirm(`Enable account for ${assignment.studentName}?`)) {
                                toggleAccountStatus(true, assignment);
                              }
                            }}
                            title="Enable Account"
                          >
                            <i className="bi bi-unlock"></i>
                          </button>
                        ) : (
                          <button
                            className="btn btn-danger"
                            onClick={() => handleDisableAccount(assignment)}
                            title="Disable Account"
                          >
                            <i className="bi bi-lock"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedAssignment && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Collect Fee Payment</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowPaymentModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <h6>{selectedAssignment.studentName}</h6>
                  <p className="text-muted mb-2">
                    Roll No: {selectedAssignment.rollNumber} | Class: {selectedAssignment.studentClass}
                  </p>
                  <div className="alert alert-info">
                    <div className="d-flex justify-content-between">
                      <span>Total Amount:</span>
                      <strong>{formatCurrency(selectedAssignment.totalAmount)}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Already Paid:</span>
                      <strong className="text-success">{formatCurrency(selectedAssignment.totalPaid)}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Pending Amount:</span>
                      <strong className="text-danger">{formatCurrency(selectedAssignment.totalPending)}</strong>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Payment Amount *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    min="0"
                    max={selectedAssignment.totalPending}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Payment Mode *</label>
                  <select
                    className="form-select"
                    value={paymentMode}
                    onChange={(e) => setPaymentMode(e.target.value)}
                  >
                    <option value="Cash">Cash</option>
                    <option value="Online">Online Transfer</option>
                    <option value="Cheque">Cheque</option>
                    <option value="Card">Card</option>
                    <option value="UPI">UPI</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Transaction ID (Optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter transaction/receipt ID"
                    value={transactionId}
                    onChange={(e) => setTransactionId(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowPaymentModal(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={processPayment}
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Record Payment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disable Account Modal */}
      {showDisableModal && selectedAssignment && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Disable Student Account
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowDisableModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <i className="bi bi-info-circle me-2"></i>
                  Disabling this account will prevent fee collection for this student until the account is re-enabled.
                </div>

                <div className="mb-3">
                  <h6>{selectedAssignment.studentName}</h6>
                  <p className="text-muted mb-0">
                    Roll No: {selectedAssignment.rollNumber} | Class: {selectedAssignment.studentClass}
                  </p>
                  <p className="text-muted mb-0">
                    Pending Amount: <strong className="text-danger">{formatCurrency(selectedAssignment.totalPending)}</strong>
                  </p>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">
                    Reason for Disabling <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Enter reason for disabling this account..."
                    value={disableReason}
                    onChange={(e) => setDisableReason(e.target.value)}
                  ></textarea>
                  <small className="text-muted">
                    Common reasons: TC issued, Left school, Duplicate entry, Fee dispute, etc.
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDisableModal(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => toggleAccountStatus(false)}
                  disabled={loading || !disableReason.trim()}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Disabling...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-lock me-2"></i>
                      Disable Account
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeeCollection;
